import React from "react";
import { formatMoneyByCurrency, moneyByCurrencyFrom, formatBatchAvgValue } from "../utils/currency";
import { User, PayoutEvent } from "../types/admin";
import AdminLoadFailed from "./AdminLoadFailed";

// Per-talent payout history dialog (Talent tab → Payouts). Presentational —
// the tab loads the rows. Moved out of pages/AdminDashboard.tsx (Phase C split).

interface Props {
  payoutsUser?: User;
  payoutsLoading: boolean;
  payoutsData: PayoutEvent[];
  // The history didn't load: "Couldn't load", never "No payout history".
  payoutsFailed: boolean;
  onRetry: () => void;
  onClose: () => void;
}

export default function AdminPayoutsModal({ payoutsUser, payoutsLoading, payoutsData, payoutsFailed, onRetry, onClose }: Props) {
  return (
    <div className="adm-modal-backdrop fade-in" onClick={onClose}>
      <div className="adm-modal-box adm-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="card-header-flex adm-mb-14">
          <h3 style={{ margin: 0 }}>Payout History — {payoutsUser?.name}</h3>
          <button className="adm-btn-ghost-sm" onClick={onClose}>
            ✕ Close
          </button>
        </div>
        {payoutsLoading ? (
          <p className="muted-text adm-center adm-p-20">
            Loading...
          </p>
        ) : payoutsFailed ? (
          <AdminLoadFailed what="payout history" onRetry={onRetry} />
        ) : payoutsData.length === 0 ? (
          <p className="muted-text adm-center adm-p-20">
            No payout history found.
          </p>
        ) : (
          <div className="adm-table-wrapper" style={{ maxHeight: 380, overflowY: "auto" }}>
            <table className="adm-table-light adm-table-compact sticky-header">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Gross</th>
                  <th>Net (Talent)</th>
                  <th>Orders</th>
                  <th>Accepted</th>
                  <th>Declined</th>
                  <th>Avg. Value</th>
                </tr>
              </thead>
              <tbody>
                {payoutsData.map((p) => {
                  const hasBreakdown = Object.keys(p.totalsByCurrency || {}).length > 0;
                  const grossStr = hasBreakdown
                    ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "gross"))
                    : `$${p.totalGross.toFixed(2)}`;
                  const netStr = hasBreakdown
                    ? formatMoneyByCurrency(moneyByCurrencyFrom(p.totalsByCurrency, "net"))
                    : `$${p.totalNet.toFixed(2)}`;
                  const avgStr = formatBatchAvgValue(p.totalsByCurrency, p.avgRequestValue);
                  return (
                    <tr key={p._id} className="hover-row">
                      <td>{new Date(p.createdAt).toLocaleDateString()}</td>
                      <td className="muted-text">
                        {new Date(p.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                      </td>
                      <td className="adm-semibold">{grossStr}</td>
                      <td className="adm-bold adm-green">
                        {netStr}
                      </td>
                      <td>{p.ordersCount}</td>
                      <td className="adm-green">{p.ordersAccepted}</td>
                      <td className="adm-red">{p.ordersDeclined}</td>
                      <td>{avgStr}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
